import { Component, ElementRef, OnDestroy, AfterViewInit, ViewChild, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';

import { ROUTES } from '../../../core/constants/routes.constants';
import { CardComponent } from '../../../core/components/card/card.component';
import { RingProgressComponent } from '../../../core/components/ring-progress/ring-progress.component';
import { MiniCompassComponent } from '../../../core/components/mini-compass/mini-compass.component';
import { BookmarkItem, LibraryCategory, SehriIftarInfo, TodayPrayerCheck } from '../../interfaces/dashboard.type';

interface PrayerTimeItem {
  name: string;
  time: string;
  icon: string;
  done: boolean;
  isNext?: boolean;
  bestTimeFrom?: string;
  bestTimeTo?: string;
}

interface QuickTile {
  icon: string;
  label: string;
  sub: string;
  detail: string;
  action: string;
  path: string;
  color: string;
  bgColor: string;
}

@Component({
  selector: 'app-dashboard',
  imports: [RouterLink, CardComponent, RingProgressComponent, MiniCompassComponent, TranslatePipe],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss'
})
export class DashboardComponent implements AfterViewInit, OnDestroy {
  @ViewChild('compassWrap') private compassWrapRef!: ElementRef<HTMLElement>;
  protected compassSize = signal(110);
  private resizeObserver?: ResizeObserver;
  protected readonly routes = ROUTES;
  protected readonly userName = 'User';
  protected readonly location = 'Lucknow, India';

  protected readonly gregorianDate = { day: '21', month: 'June', year: '2026', weekday: 'Sunday' };
  protected readonly currentTime = '03:45 PM';
  protected readonly hijriDate = { day: '8', month: 'Muharram', year: '1448', arabicDay: 'الأحد', arabicLabel: '(اتوار)' };
  protected readonly sunrise = '5:42 AM';
  protected readonly sunset = '6:58 PM';

  protected readonly lifetimeKaza = {
    daysSinceAge7: '13,505',
    totalMissed: '67,525',
    completed: '13,600',
    remaining: '53,925',
    percentDone: 20
  };

  protected readonly todayProgressPercent = 40;

  protected readonly todayChecklist: TodayPrayerCheck[] = [
    { name: 'Tahajjud', done: null },
    { name: 'Fajr', done: true },
    { name: 'Dhuhr', done: false },
    { name: 'Asr', done: false },
    { name: 'Maghrib', done: false },
    { name: 'Isha', done: false }
  ];

  protected readonly kazaNamaz = { total: 12, completed: 5 };

  protected get kazaNamazPercent(): number {
    return (this.kazaNamaz.completed / this.kazaNamaz.total) * 100;
  }

  protected get kazaNamazLabel(): string {
    return `${Math.round(this.kazaNamazPercent)}%`;
  }

  protected readonly upcomingPrayer = {
    name: 'MAGHRIB',
    time: '06:45 PM',
    countdown: '1h 4m left',
    until: 'Until Maghrib',
    bestTime: 'Immediately – 07:05 PM'
  };

  protected readonly prayerTimes: PrayerTimeItem[] = [
    { name: 'Fajr', time: '04:35 AM', icon: '🌙', done: true, bestTimeFrom: '04:50 AM', bestTimeTo: '06:05 AM' },
    { name: 'Dhuhr', time: '12:22 PM', icon: '☀️', done: false, bestTimeFrom: '12:30 PM', bestTimeTo: '01:15 PM' },
    { name: 'Asr', time: '03:45 PM', icon: '🌤️', done: false, bestTimeFrom: '03:50 PM', bestTimeTo: '05:15 PM' },
    { name: 'Maghrib', time: '06:45 PM', icon: '🌅', done: false, isNext: true, bestTimeFrom: 'Immediately', bestTimeTo: '07:05 PM' },
    { name: 'Isha', time: '08:05 PM', icon: '🌙', done: false, bestTimeFrom: '08:15 PM', bestTimeTo: '09:30 PM' }
  ];

  protected readonly weather = {
    location: 'Lucknow, India',
    temperature: 32,
    condition: 'Partly Cloudy',
    feelsLike: 36,
    humidity: 48,
    wind: '12 km/h NE',
    uv: '6 (High)'
  };

  protected readonly fasting = {
    ramadanDay: 18,
    sehriTime: '04:55 AM',
    iftarTime: '06:45 PM',
    duration: '13h 50m'
  };

  protected readonly quickTiles: QuickTile[] = [
    {
      icon: '📖', label: 'dashboard.quick_tiles.quran.label', sub: 'dashboard.quick_tiles.quran.sub',
      detail: 'dashboard.quick_tiles.quran.detail', action: 'dashboard.quick_tiles.quran.action',
      path: ROUTES.DASHBOARD_QURAN, color: '#92400e', bgColor: '#fef3c7'
    },
    {
      icon: '📿', label: 'dashboard.quick_tiles.tasbeeh.label', sub: 'dashboard.quick_tiles.tasbeeh.sub',
      detail: 'dashboard.quick_tiles.tasbeeh.detail', action: 'dashboard.quick_tiles.tasbeeh.action',
      path: ROUTES.DASHBOARD_TASBEEH_COUNTER, color: '#065f46', bgColor: '#d1fae5'
    },
    {
      icon: '🤲', label: 'dashboard.quick_tiles.dua.label', sub: 'dashboard.quick_tiles.dua.sub',
      detail: 'dashboard.quick_tiles.dua.detail', action: 'dashboard.quick_tiles.dua.action',
      path: ROUTES.DASHBOARD_DUAS_AZKAR, color: '#5b21b6', bgColor: '#ede9fe'
    },
    {
      icon: '🔥', label: 'dashboard.quick_tiles.streak.label', sub: 'dashboard.quick_tiles.streak.sub',
      detail: 'dashboard.quick_tiles.streak.detail', action: 'dashboard.quick_tiles.streak.action',
      path: ROUTES.DASHBOARD_PRAYER_TIMES, color: '#991b1b', bgColor: '#fee2e2'
    }
  ];

  protected readonly sehriIftar: SehriIftarInfo = {
    sehriEnds: '04:15 AM',
    iftarTime: '06:52 PM',
    location: 'Lucknow, India'
  };

  protected readonly dailyReminder = {
    quote: 'So remember Me; I will remember you.',
    reference: 'Surah Al-Baqarah (2:152)'
  };

  protected readonly qiblaDirection = { degrees: 293, direction: 'North West' };

  protected readonly libraryCategories: LibraryCategory[] = [
    { icon: '📖', label: 'dashboard.library.books.label', count: 'dashboard.library.books.count', path: ROUTES.DASHBOARD_ISLAMIC_LIBRARY },
    { icon: '📜', label: 'dashboard.library.tafseer.label', count: 'dashboard.library.tafseer.count', path: ROUTES.DASHBOARD_ISLAMIC_LIBRARY },
    { icon: '☪️', label: 'dashboard.library.hadith.label', count: 'dashboard.library.hadith.count', path: ROUTES.DASHBOARD_ISLAMIC_LIBRARY },
    { icon: '🧑', label: 'dashboard.library.biography.label', count: 'dashboard.library.biography.count', path: ROUTES.DASHBOARD_ISLAMIC_LIBRARY },
    { icon: '⚖️', label: 'dashboard.library.fiqh.label', count: 'dashboard.library.fiqh.count', path: ROUTES.DASHBOARD_ISLAMIC_LIBRARY },
    { icon: '🕌', label: 'dashboard.library.history.label', count: 'dashboard.library.history.count', path: ROUTES.DASHBOARD_ISLAMIC_LIBRARY }
  ];

  protected readonly nearbyMasjid = { name: 'Masjid Ayesha', distance: '1.2 km' };

  protected readonly continueReading = {
    title: 'Riyad as-Salihin',
    author: 'Imam An-Nawawi',
    chapter: 'Good Character',
    progress: 40
  };

  protected readonly bookmarks: BookmarkItem[] = [
    { title: 'Surah Al-Kahf (18:45)', subtitle: 'Al-Quran', path: ROUTES.DASHBOARD_QURAN },
    { title: 'Sahih Bukhari - Book of Faith', subtitle: 'Hadith', path: ROUTES.DASHBOARD_ISLAMIC_LIBRARY },
    { title: 'Tafseer Ibn Kathir - Surah Al-Baqarah', subtitle: 'Tafseer', path: ROUTES.DASHBOARD_ISLAMIC_LIBRARY }
  ];

  protected readonly kazaOverview = { total: 12, completed: 8 };

  protected get kazaOverviewPercent(): number {
    return (this.kazaOverview.completed / this.kazaOverview.total) * 100;
  }

  protected get kazaOverviewPercentLabel(): string {
    return `${Math.round(this.kazaOverviewPercent)}%`;
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      const size = Math.floor(Math.min(width, height));
      if (size > 0) this.compassSize.set(size);
    });
    this.resizeObserver.observe(this.compassWrapRef.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }
}
